import { useState, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { 
  CheckCircle2, FileCheck, AlertTriangle, Zap, Shield, 
  TrendingDown, Clock, BarChart3, Target, Lock, Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// --- Types ---

interface DecisionThesisData {
  // Phase 1.1
  kernsatz?: string;
  killCriterion?: string;
  messsignal?: string;
  deadline?: string;
  validator?: string;
  reversibilityMarker?: string;
  // Phase 1.2.2
  tensionDelta?: {
    costOfA?: string;
    costOfB?: string;
    inactionCostMonthly?: number;
    inactionCostTotal?: number;
    commitmentDecision?: string;
    wastedCapacity?: number;
  };
  // Phase 1.3 (full)
  irreversibility?: {
    validationMetric?: string;
    validationThreshold?: string;
    validationDeadlineDays?: number;
    pivotCalendarMonths?: number;
    pivotCostEur?: number;
    commitDecision?: string;
    lockInTypes?: string[];
    lockInConcreteStep?: string;
    structuralUrgencyScore?: number;
    structuralUrgencyLevel?: string;
    expectedExposure?: number;
    adjustedPivotCost?: number;
    dailyExposure?: number;
    pivotDevMonths?: number;
    exposureUntilValidation?: number;
    exposureUntilPivot?: number;
  };
  // Onboarding
  optionALabel?: string;
  optionBLabel?: string;
  worstCase?: string;
  reversibility?: string;
  urgencyScore?: number;
  // Phase 1.2
  capacityPercent?: number;
  kpiMetric?: string;
  kpiFrom?: string;
  kpiTo?: string;
}

interface Phase14DecisionThesisProps {
  onValueChange: (value: string) => void;
  thesisData: DecisionThesisData;
  className?: string;
}

// --- Helpers ---

const EUR = (v?: number) => v != null
  ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
  : '—';

const LOCK_IN_LABELS: Record<string, string> = {
  architecture: 'Architektur',
  icp: 'ICP',
  sales: 'Sales',
  narrative: 'Narrativ',
  team: 'Team',
};

const REVERSIBILITY_LABELS: Record<string, string> = {
  leicht: 'Leicht reversibel',
  mittel: 'Mittel',
  schwer: 'Schwer reversibel',
  irreversibel: 'Irreversibel',
};

// --- Sub-Components ---

const SectionCard = ({ 
  title, icon, children, variant = 'default' 
}: { 
  title: string; 
  icon: React.ReactNode; 
  children: React.ReactNode;
  variant?: 'default' | 'warning' | 'critical';
}) => (
  <Card className={cn(
    "border transition-all",
    variant === 'critical' && "border-destructive/40 bg-destructive/5",
    variant === 'warning' && "border-amber-500/40 bg-amber-500/5",
    variant === 'default' && "border-border/50"
  )}>
    <CardHeader className="pb-3 pt-4 px-4">
      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-foreground">
        {icon}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="px-4 pb-4 pt-0">
      {children}
    </CardContent>
  </Card>
);

const DataRow = ({ label, value, emphasis }: { label: string; value?: string | React.ReactNode; emphasis?: boolean }) => (
  <div className="flex justify-between items-start gap-3 py-1.5">
    <span className="text-xs text-muted-foreground shrink-0">{label}</span>
    <span className={cn(
      "text-sm text-right",
      emphasis ? "font-semibold text-foreground" : "text-foreground/80"
    )}>
      {value || '—'}
    </span>
  </div>
);

const ConsistencyWarning = ({ message }: { message: string }) => (
  <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10">
    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
    <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">{message}</p>
  </div>
);

// --- Urgency Visualisation ---

const UrgencyDisplay = ({ score, level }: { score?: number; level?: string }) => {
  if (score == null) return null;
  const color = level === 'red' ? 'destructive' : level === 'yellow' ? 'amber' : 'emerald';
  const labelText = level === 'red'
    ? 'Kritisch — Irreversibilität droht vor Validierung'
    : level === 'yellow'
    ? 'Eingeschränkter Handlungsspielraum'
    : 'Strategischer Spielraum vorhanden';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Structural Urgency</span>
        <span className={cn(
          "text-sm font-bold",
          color === 'destructive' && "text-destructive",
          color === 'amber' && "text-amber-600 dark:text-amber-400",
          color === 'emerald' && "text-emerald-600 dark:text-emerald-400",
        )}>
          {score.toFixed(1)} / 10
        </span>
      </div>
      <Progress 
        value={score * 10} 
        className={cn(
          "h-2",
          color === 'destructive' && "[&>div]:bg-destructive",
          color === 'amber' && "[&>div]:bg-amber-500",
          color === 'emerald' && "[&>div]:bg-emerald-500",
        )} 
      />
      <p className={cn(
        "text-xs font-medium",
        color === 'destructive' && "text-destructive animate-pulse",
        color === 'amber' && "text-amber-600 dark:text-amber-400",
        color === 'emerald' && "text-emerald-600 dark:text-emerald-400",
      )}>
        {labelText}
      </p>
    </div>
  );
};

// --- Main Component ---

export const Phase14DecisionThesis = ({
  onValueChange,
  thesisData,
  className
}: Phase14DecisionThesisProps) => {
  const [checkboxConfirmed, setCheckboxConfirmed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const d = thesisData;
  const irr = d.irreversibility;
  const td = d.tensionDelta;

  // --- Consistency Checks ---
  const consistencyChecks = useMemo(() => {
    const warnings: string[] = [];

    // Check 1: Commitment consistency
    if (td?.commitmentDecision && irr?.commitDecision) {
      const c1 = td.commitmentDecision;
      const c2 = irr.commitDecision;
      if ((c1 === 'commit' && c2 === 'reduce_risk') || (c1 === 'wait' && c2 === 'commit')) {
        warnings.push('Eure Commitment-Entscheidungen aus Phase 1.2.2 und 1.3 widersprechen sich.');
      }
    }

    // Check 2: Deadline vs PNR
    if (irr?.validationDeadlineDays && irr?.pivotCalendarMonths) {
      const deadlineMonths = irr.validationDeadlineDays / 30;
      if (deadlineMonths > irr.pivotCalendarMonths) {
        warnings.push('Eure Strategie wird irreversibel, bevor sie validiert ist.');
      }
    }

    // Check 3: Urgency vs Deadline
    const urgency = irr?.structuralUrgencyScore ?? d.urgencyScore;
    if (urgency != null && urgency >= 8 && irr?.validationDeadlineDays && irr.validationDeadlineDays > 90) {
      warnings.push(`Hohe Dringlichkeit (${urgency.toFixed(1)}/10), aber Validierungsdeadline beträgt ${irr.validationDeadlineDays} Tage.`);
    }

    // Check 4: Exposure ratio
    if (irr?.exposureUntilValidation && irr?.exposureUntilPivot && irr.exposureUntilPivot > 0) {
      const ratio = irr.exposureUntilValidation / irr.exposureUntilPivot;
      if (ratio > 0.5) {
        warnings.push(`Mehr als ${(ratio * 100).toFixed(0)}% des Pivot-Budgets ist allein für die Validierung gebunden.`);
      }
    }

    return warnings;
  }, [d, td, irr]);

  // --- Confirmation Level ---
  const confirmationLevel = useMemo((): 'red' | 'yellow' | 'green' => {
    const level = irr?.structuralUrgencyLevel;
    if (level === 'red') return 'red';
    if (level === 'yellow') return 'yellow';
    return 'green';
  }, [irr]);

  // --- Completeness ---
  const completeness = useMemo(() => {
    const fields = [
      d.kernsatz, d.killCriterion, d.messsignal, d.reversibilityMarker,
      irr?.validationMetric, irr?.structuralUrgencyScore != null ? 'yes' : undefined,
    ];
    return fields.filter(f => f && String(f).trim().length > 0).length;
  }, [d, irr]);

  // --- Checkbox text ---
  const checkboxText = useMemo(() => {
    if (confirmationLevel === 'red') {
      return `Ich bestätige: Wir akzeptieren eine Exposition von ${EUR(irr?.adjustedPivotCost || irr?.pivotCostEur)} und einen Point of No Return in ${irr?.pivotCalendarMonths?.toFixed(1) || '?'} Monaten bei ${irr?.lockInTypes?.length || '?'} aktiven Lock-In-Dimensionen.`;
    }
    if (confirmationLevel === 'yellow') {
      return `Ich bestätige: Wir haben die strategischen Risiken geprüft und committen uns zur Validierung bis ${irr?.validationDeadlineDays || '?'} Tage.`;
    }
    // Green level but still reference actual numbers if available
    const exposureText = irr?.exposureUntilValidation ? ` mit einer Exposition von ${EUR(irr.exposureUntilValidation)}` : '';
    return `Ich bestätige: Wir gehen dieses Risiko bewusst ein${exposureText} und akzeptieren die strategischen Konsequenzen.`;
  }, [confirmationLevel, irr]);

  // --- Serialization ---
  useEffect(() => {
    if (confirmed) {
      const serialized = JSON.stringify({
        kernsatz: d.kernsatz,
        kill_criterion: d.killCriterion,
        messsignal: d.messsignal,
        deadline: d.deadline,
        validator: d.validator,
        reversibility_marker: d.reversibilityMarker,
        tension_delta: d.tensionDelta,
        irreversibility: d.irreversibility,
        option_labels: { a: d.optionALabel, b: d.optionBLabel },
        worst_case: d.worstCase,
        consistency_checks: consistencyChecks,
        confirmation_level: confirmationLevel,
        confirmed_at: new Date().toISOString(),
      });
      onValueChange(serialized);
    }
  }, [confirmed, d, consistencyChecks, confirmationLevel, onValueChange]);

  const handleConfirm = () => setConfirmed(true);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        <div className="p-2 rounded-lg bg-primary/10">
          <FileCheck className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Executive Decision Brief</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Dein strategisches Commitment mit bekanntem Risikoprofil.
          </p>
        </div>
      </div>

      {/* Section 1: Core Decision */}
      <SectionCard title="Strategische Kernentscheidung" icon={<Target className="w-4 h-4 text-primary" />}>
        <div className="space-y-4">
          {/* Option Labels */}
          {(d.optionALabel || d.optionBLabel) && (
            <div className="flex items-center gap-2 flex-wrap">
              {d.optionALabel && (
                <Badge variant="secondary" className="text-xs font-medium">A: {d.optionALabel}</Badge>
              )}
              <span className="text-xs text-muted-foreground font-medium">vs.</span>
              {d.optionBLabel && (
                <Badge variant="secondary" className="text-xs font-medium">B: {d.optionBLabel}</Badge>
              )}
            </div>
          )}

          {/* Wette — structured breakdown */}
          {d.kernsatz && (
            <div className="space-y-3">
              <div className="border-l-2 border-primary pl-4 py-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Wette</p>
                <p className="text-base font-semibold text-foreground leading-relaxed">
                  {d.kernsatz.split(/\.\s*Annahmen:|;\s*Risiko:/i)[0]?.trim() || d.kernsatz}
                </p>
              </div>

              {/* Extract assumptions if embedded in kernsatz */}
              {d.kernsatz.includes('Annahmen:') && (
                <div className="pl-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Kernannahmen</p>
                  <ul className="space-y-1">
                    {d.kernsatz
                      .split(/Annahmen:\s*/i)[1]
                      ?.split(/[.;]\s*Risiko:/i)[0]
                      ?.split(/[.;]\s*/)
                      .filter(s => s.trim().length > 3)
                      .slice(0, 3)
                      .map((assumption, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                          <span className="text-primary mt-0.5 shrink-0">•</span>
                          <span>{assumption.trim().replace(/\.$/, '')}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Extract risk if embedded in kernsatz */}
              {d.kernsatz.includes('Risiko:') && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-0.5">Dominantes Risiko</p>
                    <p className="text-sm text-foreground/90">
                      {d.kernsatz.split(/Risiko:\s*/i)[1]?.replace(/\.$/, '').trim()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Kipppunkt */}
          {d.killCriterion && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-destructive/5 border border-destructive/20">
              <AlertTriangle className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-destructive mb-0.5">Kipppunkt</p>
                <p className="text-sm text-foreground">{d.killCriterion}</p>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Section 2: Validation Plan */}
      <SectionCard title="Validierungsplan" icon={<BarChart3 className="w-4 h-4 text-primary" />}>
        <div className="space-y-1 divide-y divide-border/30">
          <DataRow label="Messgröße" value={irr?.validationMetric} emphasis />
          <DataRow label="Mindestwert" value={irr?.validationThreshold} />
          <DataRow label="Deadline" value={irr?.validationDeadlineDays ? `${irr.validationDeadlineDays} Tage` : d.deadline ? `${d.deadline} Tage` : undefined} />
          <DataRow label="Erfolgskriterium" value={d.messsignal} />
          {irr?.exposureUntilValidation != null && (
            <DataRow 
              label="Exposition bis Validierung" 
              value={
                <span>
                  {EUR(irr.exposureUntilValidation)}
                  {irr.dailyExposure != null && (
                    <span className="text-xs text-destructive/80 font-medium ml-1">
                      — {EUR(irr.dailyExposure)} pro Tag ohne Validierung
                    </span>
                  )}
                </span>
              }
              emphasis 
            />
          )}
          {irr?.expectedExposure != null && (
            <DataRow 
              label="Gewichtete Exposition" 
              value={EUR(irr.expectedExposure)}
            />
          )}
        </div>
      </SectionCard>

      {/* Section 3: Risk Dashboard */}
      <SectionCard 
        title="Risiko-Dashboard" 
        icon={<Shield className="w-4 h-4 text-primary" />}
        variant={confirmationLevel === 'red' ? 'critical' : confirmationLevel === 'yellow' ? 'warning' : 'default'}
      >
        <div className="space-y-4">
          <UrgencyDisplay 
            score={irr?.structuralUrgencyScore} 
            level={irr?.structuralUrgencyLevel} 
          />

          {/* Lock-In Badges */}
          {irr?.lockInTypes && irr.lockInTypes.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                {irr.lockInTypes.length} von 5 Lock-In-Dimensionen aktiv
              </p>
              <div className="flex flex-wrap gap-1.5">
                {irr.lockInTypes.map(t => (
                  <Badge key={t} variant="secondary" className="text-xs gap-1">
                    <Lock className="w-3 h-3" />
                    {LOCK_IN_LABELS[t] || t}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {irr?.lockInConcreteStep && (
            <DataRow label="Konkreter Lock-In-Schritt" value={irr.lockInConcreteStep} emphasis />
          )}

          <div className="space-y-1 divide-y divide-border/30">
            <DataRow 
              label="Point of No Return" 
              value={irr?.pivotCalendarMonths != null ? `${irr.pivotCalendarMonths.toFixed(1)} Monate` : undefined}
              emphasis 
            />
            <DataRow 
              label="Pivot-Kosten" 
              value={EUR(irr?.pivotCostEur)}
            />
            {irr?.adjustedPivotCost != null && irr.adjustedPivotCost !== irr.pivotCostEur && (
              <DataRow 
                label="Bereinigter Pivot-Schaden" 
                value={
                  <span className="text-destructive font-semibold">{EUR(irr.adjustedPivotCost)}</span>
                }
                emphasis
              />
            )}
            {d.reversibility && (
              <DataRow 
                label="Reversibilität" 
                value={REVERSIBILITY_LABELS[d.reversibility] || d.reversibility}
              />
            )}
          </div>

          {/* Consistency Warnings */}
          {consistencyChecks.length > 0 && (
            <div className="space-y-2 pt-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Info className="w-3 h-3" /> System-Konsistenzprüfung
              </p>
              {consistencyChecks.map((msg, i) => (
                <ConsistencyWarning key={i} message={msg} />
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {/* Section 4: Economic Consequence */}
      <SectionCard title="Ökonomische Konsequenz" icon={<TrendingDown className="w-4 h-4 text-primary" />}>
        <div className="space-y-1 divide-y divide-border/30">
          {td?.inactionCostMonthly != null && td.inactionCostMonthly > 0 && (
            <DataRow label="Monatliche Inaction-Kosten" value={EUR(td.inactionCostMonthly)} emphasis />
          )}
          {td?.inactionCostTotal != null && td.inactionCostTotal > 0 && (
            <DataRow label="Kumulative Gesamtkosten" value={EUR(td.inactionCostTotal)} />
          )}
          {td?.wastedCapacity != null && td.wastedCapacity > 0 && (
            <DataRow label="Kapazitätsverschwendung" value={`${td.wastedCapacity.toFixed(1)} Dev-Monate`} />
          )}
          {d.capacityPercent != null && (
            <DataRow label="Kapazitätsbindung" value={`${d.capacityPercent}%`} />
          )}
          {d.kpiMetric && (
            <DataRow 
              label="KPI-Impact" 
              value={`${d.kpiMetric}: ${d.kpiFrom || '?'} → ${d.kpiTo || '?'}`}
              emphasis
            />
          )}
          {d.worstCase && (
            <DataRow label="Worst-Case-Szenario" value={d.worstCase} />
          )}
          {td?.commitmentDecision && (
            <DataRow 
              label="Commitment (1.2.2)" 
              value={td.commitmentDecision === 'commit' ? '✓ Commitment' : '⚠️ Bewusstes Warten'}
            />
          )}
          {irr?.commitDecision && (
            <DataRow 
              label="Commitment (1.3)" 
              value={irr.commitDecision === 'commit' ? '✓ Commitment' : '⚠️ Risiko reduzieren'}
            />
          )}
        </div>
      </SectionCard>

      {/* Section 5: Confirmation Gate */}
      <SectionCard 
        title="Bestätigung" 
        icon={<CheckCircle2 className="w-4 h-4 text-primary" />}
        variant={confirmed ? 'default' : confirmationLevel === 'red' ? 'critical' : confirmationLevel === 'yellow' ? 'warning' : 'default'}
      >
        {confirmed ? (
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div>
              <p className="text-base font-semibold text-emerald-700 dark:text-emerald-300">
                Executive Decision Brief bestätigt
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Drücke jetzt "These bestätigen & weiter" um zu Phase 2 fortzufahren.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Completeness indicator */}
            {completeness < 4 && (
              <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5" />
                {completeness} von 6 Kernfeldern ausgefüllt.
              </div>
            )}

            {/* Checkbox gate */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="thesis-confirm"
                checked={checkboxConfirmed}
                onCheckedChange={(c) => setCheckboxConfirmed(c === true)}
                className="mt-0.5"
              />
              <label 
                htmlFor="thesis-confirm" 
                className={cn(
                  "text-sm leading-relaxed cursor-pointer select-none",
                  confirmationLevel === 'red' ? "text-destructive" : "text-foreground/80"
                )}
              >
                {checkboxText}
              </label>
            </div>

            <Button
              onClick={handleConfirm}
              size="lg"
              className="w-full text-base font-semibold"
              disabled={!checkboxConfirmed}
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              These bestätigen
            </Button>
          </div>
        )}
      </SectionCard>
    </div>
  );
};
